import { supabase } from '@/lib/supabase';
import { getTenantId } from '@/repos/contentRepo';

export type LiberationType = 'paid' | 'free' | 'trial';
export type AccessLevel = 'active' | 'trial' | 'expired' | 'suspended' | 'free_only';

export interface CreateEnrollmentData {
  user_id: string;
  course_id: string;
  tenant_id?: string;
  liberation_type?: LiberationType;
  access_level?: AccessLevel;
  granted_by?: string;
}

export interface UpdateEnrollmentData {
  access_level?: AccessLevel;
  status?: 'active' | 'inactive' | 'cancelled';
}

export interface EnrollmentFilters {
  user_id?: string;
  course_id?: string;
  liberation_type?: LiberationType;
  access_level?: AccessLevel;
  tenant_id?: string;
  page?: number;
  limit?: number;
}

/**
 * Cria matrícula do usuário em um curso (admin ou fluxo automático)
 */
export async function createEnrollment(data: CreateEnrollmentData): Promise<any> {
  try {
    const tenantId = data.tenant_id || await getTenantId();
    const now = new Date().toISOString();

    const insertData: Record<string, unknown> = {
      user_id: data.user_id,
      course_id: data.course_id,
      tenant_id: tenantId,
      access_level: data.access_level || 'active',
      started_at: now,
    };

    // Novos campos (após migration add-enrollment-fields.sql)
    if (data.liberation_type) insertData.liberation_type = data.liberation_type;
    if (data.granted_by) {
      insertData.granted_by = data.granted_by;
      insertData.granted_at = now;
    }

    const { data: enrollment, error } = await supabase.rpc('major_high_manage_academic_enrollment_v1', {
      p_operation: 'ENROLL_STUDENT_IN_COURSE', p_user_id: data.user_id, p_course_id: data.course_id,
    });

    if (error) throw error;

    console.log('✅ Matrícula criada:', enrollment?.id);
    return enrollment;
  } catch (error) {
    console.error('❌ Erro ao criar matrícula:', error);
    throw error;
  }
}

/**
 * Atualiza uma matrícula existente
 */
export async function updateEnrollment(enrollmentId: string, data: UpdateEnrollmentData): Promise<any> {
  try {
    const updateData: Record<string, unknown> = {};
    if (data.access_level !== undefined) updateData.access_level = data.access_level;

    const { data: enrollment, error } = await supabase
      .from('enrollments')
      .update(updateData)
      .eq('id', enrollmentId)
      .select()
      .single();

    if (error) throw error;

    console.log('✅ Matrícula atualizada:', enrollment?.id);
    return enrollment;
  } catch (error) {
    console.error('❌ Erro ao atualizar matrícula:', error);
    throw error;
  }
}

/**
 * Cancela uma matrícula (soft delete via status)
 */
export async function cancelEnrollment(enrollmentId: string): Promise<void> {
  try {
    const { data: row, error: lookupError } = await supabase.from('enrollments').select('user_id,course_id').eq('id', enrollmentId).single();
    if (lookupError) throw lookupError;
    const { error } = await supabase.rpc('major_high_manage_academic_enrollment_v1', {
      p_operation: 'UNENROLL_STUDENT_FROM_COURSE', p_user_id: row.user_id, p_course_id: row.course_id,
    });

    if (error) throw error;

    console.log('✅ Matrícula cancelada:', enrollmentId);
  } catch (error) {
    console.error('❌ Erro ao cancelar matrícula:', error);
    throw error;
  }
}

/**
 * Lista matrículas de um usuário
 */
export async function getUserEnrollments(userId: string): Promise<any[]> {
  try {
    const tenantId = await getTenantId();
    const { data, error } = await supabase
      .from('enrollments')
      .select(`
        *,
        courses (
          id,
          title,
          slug,
          cover
        )
      `)
      .eq('user_id', userId)
      .eq('tenant_id', tenantId)
      .neq('access_level', 'expired')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('❌ Erro ao listar matrículas do usuário:', error);
    throw error;
  }
}

/**
 * Lista matrículas de um curso
 */
export async function getCourseEnrollments(courseId: string): Promise<any[]> {
  try {
    const tenantId = await getTenantId();
    const { data, error } = await supabase
      .from('enrollments')
      .select(`
        *,
        users!enrollments_user_id_fkey (
          id,
          name,
          email
        )
      `)
      .eq('course_id', courseId)
      .eq('tenant_id', tenantId)
      .neq('access_level', 'expired')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('❌ Erro ao listar matrículas do curso:', error);
    throw error;
  }
}

/**
 * Lista matrículas para admin com filtros e paginação
 */
export async function getEnrollmentsForAdmin(filters: EnrollmentFilters = {}): Promise<{
  enrollments: any[];
  total: number;
}> {
  try {
    const tenantId = filters.tenant_id || await getTenantId();
    const page = filters.page || 1;
    const limit = filters.limit || 50;
    const offset = (page - 1) * limit;

    let query = supabase
      .from('enrollments')
      .select(`
        *,
        users!enrollments_user_id_fkey (
          id,
          name,
          email
        ),
        courses (
          id,
          title,
          slug,
          cover
        )
      `, { count: 'exact' })
      .eq('tenant_id', tenantId)
      .neq('access_level', 'expired');

    if (filters.user_id) {
      query = query.eq('user_id', filters.user_id);
    }
    if (filters.course_id) {
      query = query.eq('course_id', filters.course_id);
    }
    if (filters.liberation_type) {
      query = query.eq('liberation_type', filters.liberation_type);
    }
    if (filters.access_level) {
      query = query.eq('access_level', filters.access_level);
    }

    const { data, error, count } = await query
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    if (error) throw error;

    return {
      enrollments: data || [],
      total: count || 0,
    };
  } catch (error) {
    console.error('❌ Erro ao listar matrículas para admin:', error);
    throw error;
  }
}

/**
 * Verifica se usuário já está matriculado no curso
 */
export async function isUserEnrolled(userId: string, courseId: string): Promise<boolean> {
  try {
    const tenantId = await getTenantId();
    const { data, error } = await supabase
      .from('enrollments')
      .select('id')
      .eq('user_id', userId)
      .eq('course_id', courseId)
      .eq('tenant_id', tenantId)
      .in('access_level', ['active', 'trial'])
      .maybeSingle();

    if (error) throw error;
    return !!data;
  } catch (error) {
    console.error('❌ Erro ao verificar matrícula:', error);
    return false;
  }
}
